<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import SupervisorCard from '@/components/dashboard/SupervisorCard.vue'
import SupervisorMetric from '@/components/dashboard/SupervisorMetric.vue'
import SupervisorRow from '@/components/dashboard/SupervisorRow.vue'
import SupervisorAction from '@/components/dashboard/SupervisorAction.vue'

type Payload = {
  supervisor?: Record<string, any>
  stats?: Record<string, any>
  active_job?: Record<string, any> | null
  crew?: Array<Record<string, any>>
  reports?: Array<Record<string, any>>
  tank_alerts?: Array<Record<string, any>>
  sheets?: Array<Record<string, any>>
  tickets?: Array<Record<string, any>>
  safety?: Array<Record<string, any>>
  dispatches?: Array<Record<string, any>>
  activity?: Array<Record<string, any>>
}

const router = useRouter()
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const updatedAt = ref(new Date())

const dashboard = ref<Payload>({
  supervisor: {
    name: 'Field Supervisor',
    company_name: 'Demo Oil Services Inc.',
    level: 'Supervisor',
    location: 'North Dakota',
  },
  stats: {
    crew_on_site: 0,
    reports_due: 0,
    tank_alerts: 0,
    sheets_due: 0,
    tickets_pending: 0,
    safety_items: 0,
  },
  active_job: null,
  crew: [],
  reports: [],
  tank_alerts: [],
  sheets: [],
  tickets: [],
  safety: [],
  dispatches: [],
  activity: [],
})

const stats = computed(() => dashboard.value.stats || {})
const crew = computed(() => dashboard.value.crew || [])
const reports = computed(() => dashboard.value.reports || [])
const tankAlerts = computed(() => dashboard.value.tank_alerts || [])
const sheets = computed(() => dashboard.value.sheets || [])
const tickets = computed(() => dashboard.value.tickets || [])
const safety = computed(() => dashboard.value.safety || [])
const dispatches = computed(() => dashboard.value.dispatches || [])
const activity = computed(() => dashboard.value.activity || [])

const today = computed(() =>
  updatedAt.value.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric'
  })
)

const routeMap: Record<string, string> = {
  '/supervisor/onsite': '/employee/onsite',
  '/supervisor/daily-report': '/employee/daily-report',
  '/supervisor/tanks': '/employee/tanks',
  '/supervisor/tank-watch': '/employee/tank-watch',
  '/supervisor/sheets': '/employee/tank-sheets',
  '/supervisor/field-tickets': '/employee/field-tickets',
  '/supervisor/safety': '/safety',
  '/supervisor/tank-sheets': '/employee/tank-sheets',
  '/supervisor/well-head-sheets': '/employee/well-head-sheets',
  '/supervisor/dispatch': '/employee/dispatch',
}

function go(path: string) {
  router.push(routeMap[path] || path)
}

async function loadDashboard(showSpinner = true) {
  if (showSpinner) loading.value = true
  refreshing.value = !showSpinner
  error.value = ''

  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch('/api/supervisor/dashboard', {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
    const raw = await response.text()
    let payload: any = {}
    try {
      payload = raw ? JSON.parse(raw) : {}
    } catch {
      throw new Error('Supervisor API did not return JSON. Refresh and try again.')
    }
    if (!response.ok) throw new Error(payload.error || 'Unable to load supervisor dashboard')

    dashboard.value = {
      ...dashboard.value,
      ...payload,
      supervisor: { ...dashboard.value.supervisor, ...(payload.supervisor || payload.user || {}) },
      stats: { ...dashboard.value.stats, ...(payload.stats || {}) },
    }
  } catch (e: any) {
    error.value = e?.message || 'Supervisor dashboard data could not be loaded.'
  } finally {
    loading.value = false
    refreshing.value = false
    updatedAt.value = new Date()
  }
}

onMounted(() => loadDashboard())
</script>

<template>
  <AppLayout page-title="Supervisor Command Center" page-subtitle="Crew, reports, tanks, and tickets">
    <div class="mx-auto w-full max-w-[1700px]">
        <!-- Supervisor command strip -->
        <section class="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-700/50 bg-slate-800/45 p-4 shadow-xl shadow-black/10 backdrop-blur-xl xl:flex-row xl:items-center xl:justify-between">
          <div class="flex min-w-0 items-center gap-3">
            <div class="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-emerald-400 font-black text-slate-950">
              SV
            </div>
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h1 class="truncate text-2xl font-extrabold tracking-tight text-white">Supervisor Command Center</h1>
                <span class="rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-300">
                  {{ dashboard.supervisor?.level || 'Supervisor' }}
                </span>
              </div>
              <p class="mt-1 truncate text-sm text-slate-400">
                {{ dashboard.supervisor?.name }} · {{ dashboard.supervisor?.company_name }} · {{ dashboard.supervisor?.location }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-2 text-sm">
            <div class="rounded-xl border border-slate-700/50 bg-slate-900/45 px-3 py-2">
              <span class="text-slate-500">Today</span><strong class="ml-2 text-white">{{ today }}</strong>
            </div>
            <div class="rounded-xl border border-slate-700/50 bg-slate-900/45 px-3 py-2">
              <span class="text-slate-500">Updated</span>
              <strong class="ml-2 text-white">{{ updatedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) }}</strong>
            </div>
            <button class="rounded-xl border border-slate-600/50 bg-slate-700/50 px-4 py-2 font-semibold hover:border-emerald-500/30 hover:text-emerald-300" @click="loadDashboard(false)">
              {{ refreshing ? 'Refreshing…' : '↻ Refresh' }}
            </button>
          </div>
        </section>

        <div v-if="error" class="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{{ error }}</div>

        <div v-if="loading" class="space-y-5">
          <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            <div v-for="n in 6" :key="n" class="h-28 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40"></div>
          </div>
          <div class="grid gap-5 xl:grid-cols-12">
            <div class="h-96 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40 xl:col-span-7"></div>
            <div class="h-96 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40 xl:col-span-5"></div>
          </div>
        </div>

        <template v-else>
          <!-- KPIs -->
          <section class="mb-5 grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
            <SupervisorMetric title="Crew On Site" :value="stats.crew_on_site ?? 0" subtitle="Current headcount" icon="crew" tone="emerald" @click="go('/supervisor/onsite')" />
            <SupervisorMetric title="Reports Due" :value="stats.reports_due ?? 0" subtitle="Daily reports" icon="report" tone="cyan" @click="go('/supervisor/daily-report')" />
            <SupervisorMetric title="Tank Alerts" :value="stats.tank_alerts ?? 0" subtitle="High / exception" icon="tank" tone="red" @click="go('/supervisor/tanks')" />
            <SupervisorMetric title="Sheets Due" :value="stats.sheets_due ?? 0" subtitle="Tank / well head" icon="sheet" tone="amber" @click="go('/supervisor/sheets')" />
            <SupervisorMetric title="Tickets Pending" :value="stats.tickets_pending ?? 0" subtitle="Field tickets" icon="ticket" tone="purple" @click="go('/supervisor/field-tickets')" />
            <SupervisorMetric title="Safety Items" :value="stats.safety_items ?? 0" subtitle="JSA / incidents" icon="safety" tone="blue" @click="go('/supervisor/safety')" />
          </section>

          <!-- Active job / quick actions / critical alerts -->
          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <SupervisorCard class="xl:col-span-5" title="Active Location" subtitle="Current well and shift command" action-label="On Site Hub" @action="go('/supervisor/onsite')">
              <div v-if="dashboard.active_job" class="rounded-2xl border border-emerald-500/20 bg-emerald-500/[.06] p-5">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <span class="text-[10px] font-bold uppercase tracking-[.15em] text-emerald-300">On Location</span>
                    <h3 class="mt-1 text-xl font-extrabold text-white">{{ dashboard.active_job.well_name || dashboard.active_job.title }}</h3>
                    <p class="mt-1 text-sm text-slate-400">
                      {{ dashboard.active_job.operator || dashboard.active_job.customer }} · {{ dashboard.active_job.service_type }}
                    </p>
                  </div>
                  <span class="rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] font-bold uppercase text-emerald-300">
                    {{ dashboard.active_job.status || 'Active' }}
                  </span>
                </div>

                <div class="mt-5 grid gap-3 sm:grid-cols-3">
                  <div class="rounded-xl bg-slate-950/35 p-3">
                    <span class="block text-[10px] uppercase text-slate-500">Location</span>
                    <strong class="mt-1 block text-sm text-white">{{ dashboard.active_job.location || '—' }}</strong>
                  </div>
                  <div class="rounded-xl bg-slate-950/35 p-3">
                    <span class="block text-[10px] uppercase text-slate-500">Shift</span>
                    <strong class="mt-1 block text-sm text-white">{{ dashboard.active_job.shift || '—' }}</strong>
                  </div>
                  <div class="rounded-xl bg-slate-950/35 p-3">
                    <span class="block text-[10px] uppercase text-slate-500">Company Man</span>
                    <strong class="mt-1 block text-sm text-white">{{ dashboard.active_job.company_man || '—' }}</strong>
                  </div>
                </div>
              </div>

              <div v-else class="grid min-h-60 place-items-center rounded-xl border border-dashed border-slate-700/60 text-center">
                <div>
                  <p class="font-semibold text-white">No active location</p>
                  <p class="mt-1 text-sm text-slate-500">Assigned job information will appear here.</p>
                </div>
              </div>
            </SupervisorCard>

            <SupervisorCard class="xl:col-span-4" title="Shift Actions" subtitle="Fast access to field reporting">
              <div class="grid grid-cols-2 gap-3">
                <SupervisorAction label="Daily Report" icon="report" tone="emerald" @click="go('/supervisor/daily-report')" />
                <SupervisorAction label="Tank Sheet" icon="tank" tone="cyan" @click="go('/supervisor/tank-sheets')" />
                <SupervisorAction label="Well Head Sheet" icon="well" tone="blue" @click="go('/supervisor/well-head-sheets')" />
                <SupervisorAction label="Field Ticket" icon="ticket" tone="purple" @click="go('/supervisor/field-tickets')" />
                <SupervisorAction label="Tanks" icon="tank" tone="amber" @click="go('/supervisor/tanks')" />
                <SupervisorAction label="Tank Watch" icon="watch" tone="red" @click="go('/supervisor/tank-watch')" />
                <SupervisorAction label="JSA / Safety" icon="safety" tone="amber" @click="go('/supervisor/safety')" />
                <SupervisorAction label="Crew On Site" icon="crew" tone="emerald" @click="go('/supervisor/onsite')" />
                <SupervisorAction label="Dispatch" icon="dispatch" tone="cyan" @click="go('/supervisor/dispatch')" />
              </div>
            </SupervisorCard>

            <SupervisorCard class="xl:col-span-3" title="Critical Alerts" subtitle="Items needing immediate attention">
              <div class="space-y-2">
                <SupervisorRow v-for="(item, i) in tankAlerts.slice(0,3)" :key="`tank-${item.id || i}`" :title="item.title || item.tank_name || 'Tank Alert'" :subtitle="item.message || item.location || ''" :meta="item.time || ''" tone="red" />
                <SupervisorRow v-for="(item, i) in safety.slice(0,2)" :key="`safe-${item.id || i}`" :title="item.title || item.type || 'Safety Alert'" :subtitle="item.description || item.location || ''" :meta="item.time || ''" :tone="item.severity === 'high' ? 'red' : 'amber'" />
                <div v-if="!tankAlerts.length && !safety.length" class="grid min-h-52 place-items-center rounded-xl bg-slate-900/25 text-center">
                  <div><div class="text-2xl">✓</div><p class="mt-2 font-semibold text-white">No critical alerts</p><p class="mt-1 text-xs text-slate-500">Location exceptions will show here.</p></div>
                </div>
              </div>
            </SupervisorCard>
          </section>

          <!-- Crew + reports + tank watch -->
          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <SupervisorCard class="xl:col-span-4" title="Crew On Site" subtitle="Live shift headcount" action-label="Manage Crew" @action="go('/supervisor/onsite')">
              <div class="space-y-2">
                <SupervisorRow v-for="(person, i) in crew.slice(0,7)" :key="person.id || i" :title="person.name || `Crew Member ${i+1}`" :subtitle="[person.role, person.crew_level].filter(Boolean).join(' · ')" :meta="person.status || person.shift || ''" tone="emerald" />
                <div v-if="!crew.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">No crew currently checked in.</div>
              </div>
            </SupervisorCard>

            <SupervisorCard class="xl:col-span-4" title="Daily Report Timeline" subtitle="Scheduled reports to the Company Man" action-label="Open Daily Report" @action="go('/supervisor/daily-report')">
              <div class="space-y-2">
                <SupervisorRow v-for="(item, i) in reports.slice(0,7)" :key="item.id || i" :title="item.label || item.report_time || `Report ${i+1}`" :subtitle="item.recipient ? `To ${item.recipient}` : item.well_name || ''" :meta="item.status || ''" :tone="item.status === 'sent' ? 'emerald' : item.status === 'overdue' ? 'red' : 'cyan'" />
                <div v-if="!reports.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">Scheduled report times will appear here.</div>
              </div>
            </SupervisorCard>

            <SupervisorCard class="xl:col-span-4" title="Tank Watch" subtitle="Live oil and water conditions" action-label="Open Tank Watch" @action="go('/supervisor/tank-watch')">
              <div class="grid grid-cols-2 gap-3">
                <div v-for="(tank, i) in tankAlerts.slice(0,4)" :key="tank.id || i" class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-3">
                  <div class="flex justify-between gap-2">
                    <strong class="text-sm text-white">{{ tank.tank_name || tank.name || `Tank ${i+1}` }}</strong>
                    <span class="text-xs font-bold" :class="Number(tank.fill_pct || 0) >= 85 ? 'text-red-300' : 'text-emerald-300'">{{ tank.fill_pct ?? 0 }}%</span>
                  </div>
                  <div class="mt-3 h-2 overflow-hidden rounded-full bg-slate-700/50">
                    <div class="h-full rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400" :style="{width: `${Math.min(100, Number(tank.fill_pct || 0))}%`}"></div>
                  </div>
                  <div class="mt-2 flex justify-between text-[10px] text-slate-500">
                    <span>Oil {{ tank.oil_bbl ?? 0 }}</span><span>Water {{ tank.water_bbl ?? 0 }}</span>
                  </div>
                </div>
                <div v-if="!tankAlerts.length" class="col-span-2 rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">Tank readings will appear here.</div>
              </div>
            </SupervisorCard>
          </section>

          <!-- Sheets / tickets / dispatch -->
          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <SupervisorCard class="xl:col-span-4" title="Field Sheets" subtitle="Tank and well head completion status">
              <div class="space-y-2">
                <SupervisorRow v-for="(item, i) in sheets.slice(0,6)" :key="item.id || i" :title="item.title || item.type || 'Field Sheet'" :subtitle="item.well_name || item.location || ''" :meta="item.status || item.hour || ''" :tone="item.status === 'complete' ? 'emerald' : item.status === 'overdue' ? 'red' : 'amber'" />
                <div v-if="!sheets.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">Sheet status will appear here.</div>
              </div>
            </SupervisorCard>

            <SupervisorCard class="xl:col-span-4" title="Field Ticket Queue" subtitle="Tickets to complete or review" action-label="Field Tickets" @action="go('/supervisor/field-tickets')">
              <div class="space-y-2">
                <SupervisorRow v-for="(item, i) in tickets.slice(0,6)" :key="item.id || i" :title="item.ticket_number || item.title || `Ticket ${i+1}`" :subtitle="[item.customer, item.well_name].filter(Boolean).join(' · ')" :meta="item.status || ''" :tone="item.status === 'approved' ? 'emerald' : item.status === 'rejected' ? 'red' : 'purple'" />
                <div v-if="!tickets.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">No field tickets waiting.</div>
              </div>
            </SupervisorCard>

            <SupervisorCard class="xl:col-span-4" title="Dispatch & Callouts" subtitle="Incoming and active assignments" action-label="Open Dispatch" @action="go('/supervisor/dispatch')">
              <div class="space-y-2">
                <SupervisorRow v-for="(item, i) in dispatches.slice(0,6)" :key="item.id || i" :title="item.title || item.service_type || 'Dispatch'" :subtitle="[item.well_name, item.location].filter(Boolean).join(' · ')" :meta="item.status || item.start_time || ''" :tone="item.status === 'late' ? 'red' : item.status === 'en_route' ? 'cyan' : 'emerald'" />
                <div v-if="!dispatches.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">No active dispatches.</div>
              </div>
            </SupervisorCard>
          </section>

          <!-- Recent activity -->
          <SupervisorCard title="Shift Activity" subtitle="Everything happening on the location this shift">
            <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              <SupervisorRow v-for="(item, i) in activity.slice(0,9)" :key="item.id || i" :title="item.title || item.description || 'Shift Activity'" :subtitle="item.description && item.title ? item.description : item.worker || ''" :meta="item.time || item.created_at || ''" :tone="item.status === 'danger' ? 'red' : item.status === 'warning' ? 'amber' : item.status === 'success' ? 'emerald' : 'cyan'" />
              <div v-if="!activity.length" class="col-span-full rounded-xl border border-dashed border-slate-700/60 p-8 text-center text-sm text-slate-500">Shift activity will appear here as workers update OilBuddy.</div>
            </div>
          </SupervisorCard>
        </template>
    </div>
  </AppLayout>
</template>
